import React from "react";
import { observer } from "mobx-react-lite";
import pl from "shared/messages/pl";
import { BasicTable, RegularText, TextTableHeader } from "shared/components";
import { constants } from "shared/constants";
import styles from "../commissionsTable.module.scss";
import { Commission, CommissionGroup, CommissionLine } from "view360Card/models";
import { formatPercentageNumber } from "utils/formattingUtils";
import { MobileViewCard } from "view360Card/components/mobileViewCard/MobileViewCard";

export interface CommissionsTableEhzProps {
    commissionLines: CommissionLine[];
    systemId: number | undefined;
}

export const CommissionsTableEhz: React.FC<CommissionsTableEhzProps> = observer((props) => {
    const headerItems = [
        {
            content: <TextTableHeader content={pl.view360Card.commissionTable.line} />,
            key: "line",
            className: styles.lineColumn,
        },
        {
            content: <TextTableHeader content={pl.view360Card.commissionTable.group} />,
            key: "group",
            className: styles.groupColumn,
        },
        {
            content: <TextTableHeader content={pl.view360Card.commissionTable.yearGroup} />,
            key: "yearGroup",
            className: styles.alignCenter,
        },
        {
            content: <TextTableHeader content={pl.view360Card.commissionTable.rate} />,
            key: "baseRate",
            className: styles.alignCenter,
        },
    ];

    const getRows = () => {
        return props.commissionLines.map((line) => {
            return {
                key: `${props.systemId}-line-${line.id}`,
                items: [
                    {
                        className: styles.lineColumn,
                        key: `${line.id}-line-name`,
                        content: <RegularText content={line.description} />,
                    },
                    {
                        key: `${line.id}-line-details`,
                        content: getGroupLevelTable(line.groupList ?? []),
                        className: styles.tableCell,
                    },
                ],
            };
        });
    };

    const getRateLevelTable = (Commissions: Commission[]) => {
        return <BasicTable rows={commissionLevelTableRows(Commissions)} />;
    };

    const commissionLevelTableRows = (Commissions: Commission[]) => {
        return Commissions.map((commission) => {
            return {
                key: commission.id,
                items: [
                    {
                        key: `${commission.id}-commission-yearGroup`,
                        content: (
                            <RegularText content={commission.yearGroup ? commission.yearGroup : constants.noValue} />
                        ),
                        className: styles.alignCenter,
                    },
                    {
                        key: `${commission.id}-commission-baseRate`,
                        content: <RegularText content={formatPercentageNumber(commission.baseRate)} />,
                        className: styles.alignCenter,
                    },
                ],
            };
        });
    };

    const commissionLevelTableHeaders = [
        {
            content: <TextTableHeader content={pl.view360Card.commissionTable.yearGroup} />,
            key: "yearGroup",
            className: styles.alignCenter,
        },
        {
            content: <TextTableHeader content={pl.view360Card.commissionTable.rate} />,
            key: "baseRate",
            className: styles.alignCenter,
        },
    ];

    const getGroupLevelTable = (commissionGroups: CommissionGroup[]) => {
        return <BasicTable rows={groupLevelTableRows(commissionGroups)} />;
    };

    const groupLevelTableRows = (commissionGroups: CommissionGroup[]) => {
        return commissionGroups.map((group) => {
            return {
                key: group.id,
                items: [
                    {
                        key: `${group.id}-group-name`,
                        content: <RegularText content={group.description} />,
                        className: styles.groupColumn,
                    },
                    {
                        key: `${group.id}-line-details`,
                        content: getRateLevelTable(group.commissionList ?? []),
                        className: styles.tableCell,
                    },
                ],
            };
        });
    };

    const getMobileHeader = (lineDescription: string | undefined, commisionGroup: CommissionGroup) => {
        return `${lineDescription} / ${commisionGroup.description}`;
    };

    const getMobilViewDetailItem = (commisionGroup: CommissionGroup) => {
        return [
            <BasicTable
                key={`mobileViewTable-${commisionGroup.id}`}
                headerItems={commissionLevelTableHeaders}
                rows={commissionLevelTableRows(commisionGroup.commissionList ?? [])}
            ></BasicTable>,
        ];
    };

    const getMobileViewRows = () => {
        if (!props.commissionLines) {
            return null;
        }

        var result: JSX.Element[] = [];

        props.commissionLines.forEach((line) => {
            line.groupList?.forEach((group) => {
                result.push(
                    <MobileViewCard
                        key={`${props.systemId}-${line.id}-${group.id}`}
                        header={getMobileHeader(line.description, group)}
                        detailsItems={getMobilViewDetailItem(group)}
                    />
                );
            });
        });

        return result;
    };

    return (
        <BasicTable
            key={props.systemId}
            rows={getRows()}
            headerItems={headerItems}
            mobileViewRows={getMobileViewRows()}
        />
    );
});
